import { Link } from '@tiptap/extension-link';
import { ReactRenderer } from '@tiptap/react';
import { BubbleMenuPlugin } from '@tiptap/extension-bubble-menu';
import { LinkEditor } from './LinkEditor';
import type { LinkEditorState } from './LinkEditor';
import type { LinkOptions } from '@tiptap/extension-link';

declare module '@tiptap/core' {
   interface Commands<ReturnType> {
      editableLink: {
         showLinkEditor: (state: LinkEditorState) => ReturnType;
         hideLinkEditor: () => ReturnType;
         editLink: () => ReturnType;
         insertLink: (text: string, href: string, target: string | null) => ReturnType;
      };
   }
}

interface EditableLinkOptions extends LinkOptions {
   editorId: string | null;
}

export const EditableLink = Link.extend<EditableLinkOptions>({
   inclusive: false,
   priority: 1,

   onCreate() {
      let props = { editor: this.editor };

      this.storage.renderer = new ReactRenderer(LinkEditor, {
         props,
         editor: this.editor,
      });

      this.storage.bubbleMenu = BubbleMenuPlugin({
         pluginKey: 'link-editor-bubble-menu-' + this.options.editorId,
         editor: this.editor,
         element: this.storage.renderer.element,
         shouldShow: () => true,
         tippyOptions: {
            duration: [100, 0],
            maxWidth: 'none',
            moveTransition: 'transform 0.1s ease 0.05s',
            ignoreAttributes: true,
            placement: 'bottom',
         },
      });

      this.editor.registerPlugin(this.storage.bubbleMenu);
   },
   addCommands() {
      return {
         ...this.parent?.(),
         showLinkEditor:
            (state?: LinkEditorState) =>
            ({ dispatch }) => {
               if (dispatch) {
                  // TODO DOZ-5228: ref is always null after using the React 18 renderer
                  this.storage.renderer?.ref?.show(state);
               }
               return true;
            },
         hideLinkEditor:
            () =>
            ({ dispatch }) => {
               if (dispatch) {
                  // TODO DOZ-5228: ref is always null after using the React 18 renderer
                  this.storage.renderer?.ref?.hide();
               }
               return true;
            },
         editLink:
            () =>
            ({ dispatch, commands }) => {
               if (!dispatch) {
                  return true;
               }
               commands.extendMarkRange('link');
               commands.showLinkEditor('edit');
               return true;
            },
         insertLink:
            (text: string, href: string, target: string | null) =>
            ({ dispatch, tr, chain }) => {
               if (!dispatch) {
                  return true;
               }
               const currentText = this.editor.state.doc.textBetween(
                  this.editor.state.selection.from,
                  this.editor.state.selection.to
               );

               if (text !== currentText) {
                  chain()
                     .focus()
                     .extendMarkRange('link')
                     .command(() => {
                        tr.addStoredMark(this.editor.schema.mark('link', { href, target }));
                        tr.insertText(text);
                        return true;
                     })
                     .run();
               } else {
                  chain()
                     .focus()
                     .extendMarkRange('link')
                     .setLink({ href, target })
                     .setTextSelection(this.editor.state.selection.to)
                     .run();
               }
               return true;
            },
      };
   },
   addOptions() {
      return {
         ...this.parent?.(),
         editorId: null,
      };
   },
   addStorage() {
      return {
         renderer: null,
         bubbleMenu: null,
      };
   },
   addKeyboardShortcuts() {
      return {
         'Mod-k': () => this.editor.commands.editLink(),
      };
   },
   onSelectionUpdate() {
      if (this.editor.isActive('link')) {
         this.editor.commands.showLinkEditor(null);
      } else {
         this.editor.commands.hideLinkEditor();
      }
   },
   onExit() {
      this.storage.renderer.destroy();
      this.editor.unregisterPlugin(this.storage.bubbleMenu);
   },
   addPasteRules() {
      return [];
   },
}).configure({
   openOnClick: false,
   linkOnPaste: false,
   autolink: false,
   HTMLAttributes: { target: '_self' },
});
